import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    AppBar,
    Toolbar,
    IconButton,
    CircularProgress,
    Chip,
    Button,
    Paper,
    Divider,
    Container,
    Alert,
    Stack
} from '@mui/material';
import {
    ArrowBack,
    Article,
    AutoStories,
    Report,
    DeleteForever,
    Stars,
    PersonAdd,
    Man,
    Woman,
    Wc,
    BarChart,
    Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface User {
    id: string;
    username: string;
    profilePicture?: string;
}

interface Post {
    id: string;
    content?: string;
    imageUrl?: string;
    createdAt: string;
    user: User | null;
}

interface Story {
    id: string;
    imageUrl?: string;
    videoUrl?: string;
    createdAt: string;
    user: User | null;
}

interface DeletionReason {
    reason: string;
    username: string;
    deletedAt: string;
}

interface DashboardData {
    date: string;
    newUsers: {
        total: number;
        male: number;
        female: number;
        other: number;
    };
    posts: number;
    postsData: Post[];
    stories: number;
    storiesData: Story[];
    reports: number;
    deletedAccounts: number;
    deletionReasons: DeletionReason[];
    premiumPurchases: number;
    dailyGame: {
        id: string;
        answer: string;
        wordCount: number;
        difficulty: string;
        availableAt: string; // Changed to string as it comes from JSON
        expiresAt: string;   // Changed to string as it comes from JSON
    } | null;
    gamePlayers: number;
    questionBank: {
        id: string;
        answer: string;
        difficulty: string;
        timesAsked: number;
    } | null;
    errors?: string[]; // Added to handle partial failures
}

const DailyDashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DashboardData | null>(null);
    const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
    const [analyzingAI, setAnalyzingAI] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modern clean theme colors
    const COLORS = {
        primary: '#1976d2',
        secondary: '#424242',
        background: '#f5f7fa',
        cardBg: '#ffffff',
        textPrimary: '#2c3e50',
        textSecondary: '#7f8c8d',
        success: '#2ecc71',
        warning: '#f1c40f',
        danger: '#e74c3c',
        info: '#3498db'
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/api/dashboard/daily`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching dashboard data:', error);
            setError('Dashboard verileri yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        } finally {
            setLoading(false);
        }
    };

    const analyzeWithAI = async () => {
        if (!data) return;

        try {
            setAnalyzingAI(true);
            const token = localStorage.getItem('adminToken');
            const response = await axios.post(
                `${API_BASE_URL}/api/dashboard/analyze`,
                { data },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.data.success) {
                setAiAnalysis(response.data.analysis);
            }
        } catch (error) {
            console.error('Error analyzing with AI:', error);
            alert('AI analizi sırasında hata oluştu');
        } finally {
            setAnalyzingAI(false);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'easy': return 'success';
            case 'medium': return 'warning';
            case 'hard': return 'error';
            default: return 'default';
        }
    };

    const getDifficultyLabel = (difficulty: string) => {
        switch (difficulty) {
            case 'easy': return 'Kolay';
            case 'medium': return 'Orta';
            case 'hard': return 'Zor';
            default: return difficulty;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: COLORS.background }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button variant="contained" onClick={fetchDashboardData} startIcon={<Refresh />}>
                    Tekrar Dene
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, backgroundColor: COLORS.background, minHeight: '100vh', pb: 4 }}>
            {/* Simple Clean App Bar */}
            <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: COLORS.cardBg }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: COLORS.textPrimary, fontWeight: 600 }}>
                        Günlük Rapor
                    </Typography>
                    <Chip
                        label={new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ mt: 4 }}>
                {/* Warning if partial data load */}
                {data?.errors && data.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        Bazı veriler yüklenirken sorun oluştu: {data.errors.join(', ')}. Görüntülenen veriler eksik olabilir.
                    </Alert>
                )}

                {/* Summary Cards Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: 'repeat(6, 1fr)' }, gap: 3, mb: 4 }}>
                    {[
                        { title: 'Yeni Kullanıcılar', value: data?.newUsers.total || 0, icon: <PersonAdd />, color: COLORS.primary },
                        { title: 'Postlar', value: data?.posts || 0, icon: <Article />, color: COLORS.info },
                        { title: 'Hikayeler', value: data?.stories || 0, icon: <AutoStories />, color: COLORS.success },
                        { title: 'Şikayetler', value: data?.reports || 0, icon: <Report />, color: COLORS.warning },
                        { title: 'Silinen Hesaplar', value: data?.deletedAccounts || 0, icon: <DeleteForever />, color: COLORS.danger },
                        { title: 'Premium Satış', value: data?.premiumPurchases || 0, icon: <Stars />, color: '#9c27b0' }
                    ].map((item, index) => (
                        <Box key={index}>
                            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    <Box sx={{ p: 1, borderRadius: '50%', bgcolor: `${item.color}15`, color: item.color, mb: 1 }}>
                                        {React.cloneElement(item.icon as React.ReactElement<any>, { fontSize: 'medium' })}
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.textPrimary, my: 0.5 }}>
                                        {item.value}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {item.title}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: { lg: 2 }, minWidth: 0 }}>
                        {/* User Demographics */}
                        <Card elevation={0} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Kullanıcı Dağılımı</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Stack direction="row" spacing={4} alignItems="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Man color="primary" />
                                        <Typography><strong>{data?.newUsers.male || 0}</strong> Erkek</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Woman color="secondary" sx={{ color: '#e91e63' }} />
                                        <Typography><strong>{data?.newUsers.female || 0}</strong> Kadın</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Wc color="action" />
                                        <Typography><strong>{data?.newUsers.other || 0}</strong> Diğer</Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Recent Posts */}
                        <Card elevation={0} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Son Postlar</Typography>
                                <Button size="small" onClick={() => navigate('/admin/posts')}>Tümünü Gör</Button>
                            </Box>
                            <CardContent>
                                {data?.postsData && data.postsData.length > 0 ? (
                                    <Stack spacing={2}>
                                        {data.postsData.slice(0, 5).map((post) => (
                                            <Paper key={post.id} elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #eee' }}>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Box
                                                        component="img"
                                                        src={post.user?.profilePicture || '/default-avatar.png'}
                                                        sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                                    />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                {post.user?.username || 'Bilinmeyen Kullanıcı'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {post.createdAt ? new Date(post.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>{post.content}</Typography>
                                                        {post.imageUrl && (
                                                            <Box
                                                                component="img"
                                                                src={post.imageUrl}
                                                                sx={{ mt: 1, maxHeight: 150, borderRadius: 1 }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography color="textSecondary" align="center">Bugün henüz post atılmamış.</Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recent Stories */}
                        <Card elevation={0} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Son Hikayeler</Typography>
                                <Button size="small" onClick={() => navigate('/admin/stories')}>Tümünü Gör</Button>
                            </Box>
                            <CardContent>
                                {data?.storiesData && data.storiesData.length > 0 ? (
                                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                                        {data.storiesData.map((story) => (
                                            <Box key={story.id} sx={{ minWidth: 120, width: 120 }}>
                                                <Paper elevation={0} sx={{ position: 'relative', height: 180, borderRadius: 2, overflow: 'hidden' }}>
                                                    {story.imageUrl ? (
                                                        <Box component="img" src={story.imageUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Typography variant="caption" color="white">Video</Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{
                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                        p: 1, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)'
                                                    }}>
                                                        <Typography variant="caption" color="white" sx={{ display: 'block' }} noWrap>
                                                            {story.user?.username || 'User'}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography color="textSecondary" align="center">Bugün henüz hikaye atılmamış.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: { lg: 1 }, minWidth: 0 }}>
                        {/* Actions */}
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={analyzingAI ? <CircularProgress size={20} /> : <BarChart />}
                            onClick={analyzeWithAI}
                            disabled={analyzingAI}
                            sx={{ mb: 3, py: 1.5, borderStyle: 'dashed' }}
                        >
                            {analyzingAI ? 'AI Analiz Ediyor...' : 'AI ile Analiz Raporu Oluştur'}
                        </Button>

                        {aiAnalysis && (
                            <Alert severity="info" onClose={() => setAiAnalysis(null)} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>AI Analiz Sonucu:</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{aiAnalysis}</Typography>
                            </Alert>
                        )}

                        {/* Daily Game Info */}
                        <Card elevation={0} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Günlük Oyun Bilgisi</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {data?.dailyGame ? (
                                    <Box>
                                        <Typography color="textSecondary" variant="body2" gutterBottom>Günün Cevabı:</Typography>
                                        <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>{data.dailyGame.answer}</Typography>

                                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                                            <Chip label={`${data.dailyGame.wordCount} Harf`} size="small" />
                                            <Chip
                                                label={getDifficultyLabel(data.dailyGame.difficulty)}
                                                color={getDifficultyColor(data.dailyGame.difficulty) as any}
                                                size="small"
                                            />
                                        </Box>

                                        <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 2, textAlign: 'center' }}>
                                            <Typography variant="h4" fontWeight="bold">{data.gamePlayers}</Typography>
                                            <Typography variant="caption" color="textSecondary">Bugün Oynayan Kişi Sayısı</Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography color="textSecondary">Bugün için oyun sorusu atanmamış.</Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Quick Links */}
                        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Hızlı İşlemler</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {[
                                        { label: 'Kullanıcılar', path: '/admin/users' },
                                        { label: 'Postlar', path: '/admin/posts' },
                                        { label: 'Oyun Yönetimi', path: '/admin/game-management' },
                                        { label: 'Ayarlar', path: '/admin/settings' },
                                    ].map((link) => (
                                        <Chip
                                            key={link.path}
                                            label={link.label}
                                            clickable
                                            onClick={() => navigate(link.path)}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default DailyDashboardPage;
